import { useRef, useState } from 'react'
import { PiMicrophone, PiMicrophoneSlash } from 'react-icons/pi'
import to from 'await-to-js'
import useApiService from 'services/api'
import { Button } from '@firstbite/ui'
import { Alert } from 'components/FbUI/Alert'
import { useMountEffect } from 'hooks/useMountEffect'
import { useStreamingTranscription } from 'hooks/useStreamingTranscription'
import { TranscriptAnalysis } from 'models/contacts'

const ANALYZE_DEBOUNCE_MS = 3000

/**
 * Wizard capture step: stream speech to a live transcript and surface follow-up
 * question chips. The latest analyzed fields are reported up via
 * `onPrefillChange`; the wizard's footer "Next" consumes them (it's enabled only
 * once there's something to prefill). The body owns just the Record/Stop toggle.
 */
export default function SpeakStepBody({
  onPrefillChange,
}: {
  onPrefillChange: (prefill?: TranscriptAnalysis) => void
}) {
  const api = useApiService()
  const [analysis, setAnalysis] = useState<TranscriptAnalysis | undefined>()

  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const analyzedRef = useRef('')

  const runAnalyze = async (transcript: string) => {
    if (!transcript.trim() || transcript === analyzedRef.current) return
    analyzedRef.current = transcript
    const [, result] = await to(api.analyzeTranscript(transcript))
    if (result) {
      setAnalysis(result)
      onPrefillChange(result)
    }
  }

  // Debounced live analysis. Scheduling happens in the turn-finalized handler
  // (fired from the WebSocket message handler inside useStreamingTranscription),
  // never in an effect or the render body — so it runs exactly once per
  // finalized turn and is safe under Strict Mode. Each new turn resets it.
  const {
    isRecording,
    finalTranscript,
    partialTranscript,
    error,
    start,
    stop,
  } = useStreamingTranscription({
    onTurnFinalized: (transcript) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(
        () => void runAnalyze(transcript),
        ANALYZE_DEBOUNCE_MS
      )
    },
  })

  useMountEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  })

  const handleStop = () => {
    stop()
    if (timerRef.current) clearTimeout(timerRef.current)
    // Analyze immediately on stop so even a short clip enables "Next".
    void runAnalyze(finalTranscript)
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {error && <Alert color="warning" description={error} />}

      {isRecording ? (
        <div className="flex items-start gap-2 text-sm text-fg-secondary">
          <PiMicrophone className="h-5 w-5 text-brand animate-pulse shrink-0" />
          <span>
            {
              "Speak normally. We'll suggest questions live and pull out the details when you stop."
            }
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-fg-secondary">
          <PiMicrophoneSlash className="h-5 w-5 text-fg-tertiary" />
          <span>Tap Record to start</span>
        </div>
      )}

      <div className="min-h-24 rounded-xl border border-line p-3 text-sm text-fg">
        {finalTranscript}{' '}
        <span className="text-fg-tertiary">{partialTranscript}</span>
        {!finalTranscript && !partialTranscript && (
          <span className="text-fg-tertiary">Your words will appear here…</span>
        )}
      </div>

      {analysis && analysis.follow_up_questions.length > 0 && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-fg-tertiary mb-2">
            Suggestions
          </div>
          <div className="flex flex-wrap gap-2">
            {analysis.follow_up_questions.map((q) => (
              <span
                key={q}
                className="rounded-full border border-line bg-surface-sunken px-3 py-1 text-xs text-fg-secondary"
              >
                {q}
              </span>
            ))}
          </div>
        </div>
      )}

      <Button
        className="w-full"
        type="button"
        size="lg"
        variant="primary"
        appearance={isRecording ? 'outline' : 'fill'}
        onClick={isRecording ? handleStop : () => void start()}
      >
        {isRecording ? 'Stop' : 'Record'}
      </Button>
    </div>
  )
}
